import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ZipTable from '../components/ZipTable.jsx';
import SummaryStrip from '../components/SummaryStrip.jsx';
import Footnotes from '../components/Footnotes.jsx';
import { CountyOfficialAlerts, attachOfficialAlerts } from '../components/OfficialAlerts.jsx';
import Breadcrumb from '../components/Breadcrumb.jsx';
import { tzAbbreviation } from '../lib/timezone.js';
import { INDUSTRIES, INDUSTRY_LABEL, DEFAULT_INDUSTRY } from '../lib/industries.js';
import { regionTerms } from '../lib/regions.js';

const REFRESH_MS = 5 * 60 * 1000;

const CATEGORY_LABEL = { heat: 'heat', smoke: 'wildfire smoke', wind: 'wind', stormwater: 'stormwater', cold: 'cold weather' };

const INDUSTRY_STORAGE_KEY = 'heatwatch:industry';

/**
 * Categories where this state runs on the federal/advisory or unresearched
 * fallback, not its own rule. 'national' is excluded too — that tier means
 * one standard applies uniformly everywhere by design (e.g. sports/sports-hs
 * wildfire smoke, sourced from NFHS), not that a state-specific rule is
 * missing, so it shouldn't trigger the "doesn't have its own standard"
 * banner below.
 */
function nonStateCategories(jurisdiction) {
  if (!jurisdiction) return [];
  return Object.entries(jurisdiction)
    // Canadian tiers ('provincial', 'federal') are the governing law itself, not a fallback.
    .filter(([, v]) => !['state', 'national', 'provincial', 'federal'].includes(v.tier))
    .map(([key]) => CATEGORY_LABEL[key]);
}

export default function CountyDetail() {
  const { state, county } = useParams();
  const terms = regionTerms(state);
  const isCa = terms.country === 'CA';
  const [searchParams, setSearchParams] = useSearchParams();
  const [snap, setSnap] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedZip, setSelectedZip] = useState(null);

  // URL query param wins (shareable/bookmarkable), else whatever the user
  // picked last time (localStorage), else construction — unchanged default
  // behavior for every existing link/bookmark that doesn't have ?industry=.
  const industry =
    searchParams.get('industry') ??
    (typeof localStorage !== 'undefined' && localStorage.getItem(INDUSTRY_STORAGE_KEY)) ??
    DEFAULT_INDUSTRY;

  const setIndustry = (next) => {
    localStorage.setItem(INDUSTRY_STORAGE_KEY, next);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === DEFAULT_INDUSTRY) params.delete('industry');
      else params.set('industry', next);
      return params;
    });
  };

  const load = useCallback(
    async (force = false) => {
      if (force) setRefreshing(true);
      try {
        const params = new URLSearchParams({ industry });
        if (force) params.set('refresh', '1');
        const res = await fetch(`/api/states/${state}/counties/${county}/conditions?${params}`);
        if (!res.ok) throw new Error((await res.json()).error || `HTTP ${res.status}`);
        setSnap(attachOfficialAlerts(await res.json()));
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [state, county, industry],
  );

  useEffect(() => {
    setSnap(null);
    setLoading(true);
    setSelectedZip(null);
    load();
    const id = setInterval(() => load(), REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  // Deep link from the global search bar (?zip=20711) — pre-select that ZIP
  // once its county's conditions have loaded, same query-param pattern as
  // ?industry= above.
  useEffect(() => {
    const wantedZip = searchParams.get('zip');
    if (!wantedZip || !snap) return;
    if (snap.zips.some((z) => z.zip === wantedZip)) setSelectedZip(wantedZip);
  }, [snap, searchParams]);

  // Every zip in a county shares one timezone (they're all in the same
  // county), so the first zip's is representative for the whole page.
  const timeZone = snap?.zips?.[0]?.timezone;
  const stamp = useMemo(() => {
    if (!snap?.generatedAt) return null;
    return new Date(snap.generatedAt).toLocaleString('en-US', {
      timeZone: timeZone || undefined,
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }, [snap, timeZone]);
  const tzTag = tzAbbreviation(snap?.generatedAt ? new Date(snap.generatedAt) : null, timeZone);

  return (
    <div className="app">
      <Breadcrumb
        items={[
          { label: 'All states', to: '/list' },
          { label: snap?.state ?? state.toUpperCase(), to: `/${state}` },
          { label: snap?.countyName ?? county },
        ]}
      />
      <header className="masthead">
        <div>
          <h1>{snap?.countyName ?? 'Loading…'}</h1>
          <p className="sub">
            {isCa ? (
              <>
                Census division · wet bulb temperature and environmental conditions for {snap ? snap.zipCount : '—'}{' '}
                municipalities
              </>
            ) : (
              <>
                Wet bulb temperature and {(INDUSTRY_LABEL[industry] ?? INDUSTRY_LABEL[DEFAULT_INDUSTRY]).toLowerCase()}
                -regulatory conditions for {snap ? snap.zipCount : '—'} ZIP codes
              </>
            )}
          </p>
        </div>
        <div className="masthead-actions">
          {!isCa && (
          <label className="industry-select">
            Industry
            <select value={industry} onChange={(e) => setIndustry(e.target.value)}>
              {INDUSTRIES.map((i) => (
                <option key={i.key} value={i.key}>
                  {i.label}
                </option>
              ))}
            </select>
          </label>
          )}
          <div className="stamp">
            {stamp ? (
              <>
                Updated {stamp}
                {tzTag ? ` ${tzTag}` : ''}
                <br />
                Auto-refresh every 5 min
              </>
            ) : (
              'Loading…'
            )}
          </div>
          <a
            className="btn"
            href={`/api/states/${state}/counties/${county}/export.csv?industry=${industry}`}
          >
            Export CSV
          </a>
          <button className="btn" onClick={() => load(true)} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </header>

      {error && (
        <div className="banner err">
          <strong>Could not load conditions.</strong> {error}
        </div>
      )}

      {snap?.stale && (
        <div className="banner warn">
          Upstream refresh failed; showing the last good snapshot from {stamp}
          {tzTag ? ` ${tzTag}` : ''}.
        </div>
      )}

      {snap &&
        (() => {
          const categories = nonStateCategories(snap.zips[0]?.jurisdiction);
          if (categories.length === 0) return null;
          return (
            <div className="banner warn">
              {snap.state} doesn't have its own {categories.join(', ')} standard — showing the
              federal/advisory framework for {categories.length > 1 ? 'those' : 'that'} instead.
              See each ZIP's detail panel for the citation and agency.
            </div>
          );
        })()}

      {isCa && snap && (
        <div className="banner warn">
          {snap.zips[0]?.rulesEvaluated
            ? `Canadian rules come from the ${snap.zips[0].ohsRegime?.name ?? 'provincial'} register: most are listed for reference, and numeric limits that depend on workload or equipment need a saved site with its work context. `
            : 'Canadian work rules are switched off on this server pending review, so no calculated triggers are shown. '}
          Values are Open-Meteo model output.
        </div>
      )}

      {loading && !snap && <div className="loading">Fetching conditions for all {terms.points}…</div>}

      {snap && (
        <>
          <CountyOfficialAlerts hazards={snap.hazards} timeZone={timeZone} />
          <SummaryStrip snap={snap} />
          <ZipTable zips={snap.zips} selected={selectedZip} onSelect={setSelectedZip} />
          <Footnotes industry={industry} />
        </>
      )}
    </div>
  );
}
